import React, { useEffect, useState } from "react";
import MapFullScreen from "../../map/mapFullScreen";
import useMap from "../../hooks/useOpenLayersMap";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { useUserAuth } from "../../contexts/authContext";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { ConvertDfrToFeatures, GetSource, JumpToFeatures } from "../../map/mapUtil";

export default function DfrPublish(props) {
    const MAP_ID = "map-dfr-publish";
    const map = useMap(MAP_ID, false);

    const [flight] = useState(props.flight);
    const [incident, setIncident] = useState(flight.incident);
    const [location, setLocation] = useState(flight.location);
    const [summary, setSummary] = useState(flight.summary);
    const [telemetryOrVolume, setTelemetryOrVolume] = useState("TELEMETRY");
    const [telemetryAvailability, setTelemetryAvailability] = useState(false);
    const [telemetry, setTelemetry] = useState([]);

    const [incidentError, setIncidentError] = useState("");
    const [locationError, setLocationError] = useState("");
    const [summaryError, setSummaryError] = useState("");

    const { userMapSettings, handleFailedFetch } = useUserAuth();

    useEffect(() => {
        const fetchData = async () => {
            const requestOptions = {
                method: "GET",
                headers: { "Content-Type": "application/json" }
            };
            await fetch(`/api/telemetry/get/${flight.flight_uuid}`, requestOptions)
                .then((response) => (response.ok ? response.json() : Promise.reject(response)))
                .then((data) => setTelemetry(data))
                .catch((err) => handleFailedFetch(err));
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (map) {
            map.addLayer(new VectorLayer({ source: new VectorSource(), properties: { name: "dfr" } }));
        }
    }, [map]);

    useEffect(() => {
        const telemetryAvailability = Array.isArray(telemetry) && telemetry.length > 0;
        setTelemetryOrVolume(telemetryAvailability ? "TELEMETRY" : "FLIGHT_VOLUME");
        setTelemetryAvailability(telemetryAvailability);
    }, [telemetry]);

    useEffect(() => {
        const source = GetSource(map, "dfr");
        const isTelemetry = telemetryOrVolume === "TELEMETRY";
        if (!source || (isTelemetry && !telemetryAvailability)) {
            return;
        }

        const features = ConvertDfrToFeatures({ ...flight, telemetry: isTelemetry }, telemetry, userMapSettings);
        source.clear();
        source.addFeatures(features);
        JumpToFeatures(map, features, 180);
    }, [map, telemetry, telemetryAvailability, telemetryOrVolume]);

    const handleSubmit = () => {
        setIncidentError("");
        setLocationError("");
        setSummaryError("");

        let passed = true;
        if (!incident) {
            setIncidentError("Please enter a valid Case/Incident.");
            passed = false;
        }
        if (!location) {
            setLocationError("Please enter a valid location.");
            passed = false;
        }
        if (!summary) {
            setSummaryError("Please enter a valid summary.");
            passed = false;
        }
        if (!passed) {
            return;
        }

        const dfr = {
            flight_uuid: flight.flight_uuid,
            published: true,
            incident,
            location,
            summary,
            telemetry: telemetryOrVolume === "TELEMETRY"
        };
        const requestOptions = {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(dfr)
        };
        fetch("/api/dfr/insertUpdate", requestOptions)
            .then((response) => (response.ok ? response.json() : Promise.reject(response)))
            .then(() => {
                props.fetchDFR();
                handleClose();
            })
            .catch((err) => handleFailedFetch(err));
    };

    const handleClose = () => {
        props.setDfrPublishOpen(false);
    };

    return (
        <Dialog open={true} onClose={handleClose} maxWidth="lg" PaperProps={{ sx: { minWidth: { sm: "500px" }, width: { xs: "100%", sm: "auto" } } }}>
            <DialogTitle sx={{ pb: 0 }}>DFR - Publish to Public Site</DialogTitle>
            <DialogContent sx={{ width: "100%", height: "600px", pb: 0 }}>
                <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: "12px", "& > *": { margin: "0 !important" }, pt: "20px" }}>
                    <TextField label="Flight" value={flight.name} fullWidth disabled />
                    <TextField
                        label="Case/Incident"
                        value={incident}
                        onChange={(e) => setIncident(e.target.value)}
                        error={incidentError.length > 0}
                        helperText={incidentError}
                        fullWidth
                    />
                    <TextField
                        label="Location"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        error={locationError.length > 0}
                        helperText={locationError}
                        fullWidth
                    />
                    <TextField
                        label="Summary"
                        value={summary}
                        onChange={(e) => setSummary(e.target.value)}
                        error={summaryError.length > 0}
                        helperText={summaryError}
                        fullWidth
                        multiline
                    />
                    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
                        <Box sx={{ display: "flex", gap: "5px" }}>
                            <Typography variant="body2" color="text.secondary">
                                Choose Publish Type
                            </Typography>
                            <Typography variant="body2" color={telemetryAvailability ? "success" : "error"}>
                                {telemetryAvailability ? "(telemetry available)" : "(no telemetry found)"}
                            </Typography>
                        </Box>
                        <ToggleButtonGroup
                            exclusive
                            fullWidth
                            margin="dense"
                            value={telemetryOrVolume}
                            onChange={(e) => setTelemetryOrVolume(e.target.value)}
                            color="primary"
                        >
                            <ToggleButton value="TELEMETRY" disabled={!telemetryAvailability}>
                                Telemetry
                            </ToggleButton>
                            <ToggleButton value="FLIGHT_VOLUME">Flight Volume</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    <Box sx={{ height: "100%" }}>
                        <MapFullScreen map_id={MAP_ID} map={map} />
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} sx={{ mr: "auto" }}>
                    Close
                </Button>
                <Button onClick={handleSubmit}>Submit</Button>
            </DialogActions>
        </Dialog>
    );
}
